import { useCallback, useEffect, useRef, useState } from "react";
import { BellRing, Check, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fileUrl } from "@/lib/api";
import { SOUNDS } from "@/lib/sounds";

export const usePaymentAlerts = () => {
  const [rows, setRows] = useState([]);
  const seen = useRef(null);
  const load = useCallback(() => api.get("/admin/recharges").then((r) => {
    const pending = r.data.filter((x) => x.status === "pending");
    if (seen.current !== null) {
      const fresh = pending.filter((x) => !(x.id in seen.current) || seen.current[x.id] !== (x.admin_reminded_at || x.created_at));
      if (fresh.length) {
        SOUNDS.paymentAlert();
        const isReminder = fresh.every((x) => x.id in seen.current);
        toast.warning(`${isReminder ? "⏰ അപ്രൂവൽ reminder — 5+ മിനിറ്റായി pending" : "💰 പുതിയ പേമെന്റ് അഭ്യർത്ഥന"} — ${fresh[0]?.customer_name} ₹${fresh[0]?.amount_inr}`, { duration: 12000 });
      }
    }
    seen.current = Object.fromEntries(pending.map((x) => [x.id, x.admin_reminded_at || x.created_at]));
    setRows(pending);
  }).catch(() => null), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  return { rows, load };
};

export const PaymentAlerts = ({ rows, reload }) => {
  if (!rows.length) return null;
  const act = async (id, action) => { try { await api.post(`/admin/recharges/${id}/${action}`); toast.success(action === "approve" ? "ബാലൻസ് ക്രെഡിറ്റ് ചെയ്തു — കസ്റ്റമർക്ക് അറിയിപ്പ് പോയി" : "നിരസിച്ചു"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="payment-alerts" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-2">
      <p className="text-sm font-bold text-amber-800 inline-flex items-center gap-2"><BellRing size={16} className="animate-pulse" /> പേമെന്റ് അഭ്യർത്ഥനകൾ — {rows.length} pending</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} data-testid={`payment-alert-${r.id}`} className="flex flex-wrap items-center gap-2 text-xs bg-white rounded-xl border border-amber-100 px-3 py-2">
            {r.screenshot_url ? <a data-testid={`payment-screenshot-${r.id}`} href={fileUrl(r.screenshot_url)} target="_blank" rel="noreferrer"><img src={fileUrl(r.screenshot_url)} alt="screenshot" className="w-10 h-10 rounded-lg object-cover border border-[var(--line)]" /></a> : <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-400 inline-flex items-center justify-center"><ImageIcon size={14} /></span>}
            <div className="min-w-[140px]"><p className="font-bold">{r.customer_name} <span className="text-[var(--muted-text)] font-num">@{r.username}</span></p><p className="text-[10px] text-[var(--muted-text)] font-num">WA {r.whatsapp} · {fmtDate(r.created_at)}</p></div>
            <span className="font-num font-bold">₹{r.amount_inr}</span>{r.label && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">{r.label}</span>}
            <span className="rounded-full bg-sky-50 text-sky-700 px-2 py-0.5 text-[10px] font-bold">{r.app}{r.upi_id ? ` → ${r.upi_id}` : ""}</span>
            {Date.now() - new Date(r.created_at).getTime() > 5 * 60 * 1000 && <span data-testid={`payment-overdue-${r.id}`} className="rounded-full bg-red-100 text-red-600 px-2 py-0.5 text-[10px] font-bold animate-pulse">⏰ 5m+ pending</span>}
            <span className="ml-auto inline-flex gap-1">
              <button type="button" data-testid={`payment-approve-${r.id}`} onClick={() => act(r.id, "approve")} className="btn-accent h-8 px-3 rounded-full text-[11px] font-bold inline-flex items-center gap-1"><Check size={12} /> ക്രെഡിറ്റ്</button>
              <button type="button" data-testid={`payment-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="btn-ghost h-8 w-8 rounded-full inline-flex items-center justify-center text-red-500"><X size={12} /></button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
